import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { Review, CreateReviewDto, UpdateReviewDto, QueryReviewDto, InfinityPaginationResponse, CreateVoteDto } from "../../modules/shared/model";
import { 
	createReview as createReviewAction, 
	updateReview as updateReviewAction, 
	getReviewById as getReviewByIdAction, 
	getReviews as getReviewsAction, 
	deleteReview as deleteReviewAction,
	getReviewsByUser as getReviewsByUserAction,
	getReviewsByAuthor as getReviewsByAuthorAction,
	createVote as createVoteAction,
	removeVote as removeVoteAction
} from "../Actions/ReviewActions";

export const createReview = createAsyncThunk("reviews/create", async (data: CreateReviewDto, { rejectWithValue }) => {
	try { return await createReviewAction(data); } catch (e: any) { return rejectWithValue(e?.message || "Failed to create review"); }
});

export const updateReview = createAsyncThunk("reviews/update", async ({ id, data }: { id: string; data: UpdateReviewDto }, { rejectWithValue }) => {
	try { return await updateReviewAction(id, data); } catch (e: any) { return rejectWithValue(e?.message || "Failed to update review"); }
});

export const fetchReview = createAsyncThunk("reviews/getById", async (id: string, { rejectWithValue }) => {
	try { return await getReviewByIdAction(id); } catch (e: any) { return rejectWithValue(e?.message || "Failed to fetch review"); }
});

export const fetchReviews = createAsyncThunk("reviews/get", async (query: QueryReviewDto | undefined, { rejectWithValue }) => {
	try { return await getReviewsAction(query); } catch (e: any) { return rejectWithValue(e?.message || "Failed to fetch reviews"); }
});

export const deleteReview = createAsyncThunk("reviews/delete", async (id: string, { rejectWithValue }) => {
	try { await deleteReviewAction(id); return id; } catch (e: any) { return rejectWithValue(e?.message || "Failed to delete review"); }
});

export const fetchReviewsByUser = createAsyncThunk("reviews/getByUser", async ({ userId, query }: { userId: string; query?: QueryReviewDto }, { rejectWithValue }) => {
	try { return await getReviewsByUserAction(userId, query); } catch (e: any) { return rejectWithValue(e?.message || "Failed to fetch reviews by user"); }
});

export const fetchReviewsByAuthor = createAsyncThunk("reviews/getByAuthor", async ({ authorId, query }: { authorId: string; query?: QueryReviewDto }, { rejectWithValue }) => {
	try { return await getReviewsByAuthorAction(authorId, query); } catch (e: any) { return rejectWithValue(e?.message || "Failed to fetch reviews by author"); }
});

export const createVote = createAsyncThunk("reviews/createVote", async ({ reviewId, data }: { reviewId: string; data: CreateVoteDto }, { rejectWithValue }) => {
	try { return await createVoteAction(reviewId, data); } catch (e: any) { return rejectWithValue(e?.message || "Failed to create vote"); }
});

export const removeVote = createAsyncThunk("reviews/removeVote", async (reviewId: string, { rejectWithValue }) => {
	try { return await removeVoteAction(reviewId); } catch (e: any) { return rejectWithValue(e?.message || "Failed to remove vote"); }
});

interface ReviewsState {
	items: Review[];
	current: Review | null;
	loading: boolean;
	error: string | null;
	totalCount?: number;
	hasNextPage?: boolean;
	// User-specific reviews
	userReviews: Review[];
	userReviewsLoading: boolean;
	userReviewsError: string | null;
	userReviewsTotalCount?: number;
	userReviewsHasNextPage?: boolean;
	// Author-specific reviews
	authorReviews: Review[];
	authorReviewsLoading: boolean;
	authorReviewsError: string | null;
	authorReviewsTotalCount?: number;
	authorReviewsHasNextPage?: boolean;
	// Vote operations
	voteLoading: boolean;
	voteError: string | null;
}

const initialState: ReviewsState = {
	items: [],
	current: null,
	loading: false,
	error: null,
	totalCount: 0,
	hasNextPage: false,
	// User-specific reviews
	userReviews: [],
	userReviewsLoading: false,
	userReviewsError: null,
	userReviewsTotalCount: 0,
	userReviewsHasNextPage: false,
	// Author-specific reviews
	authorReviews: [],
	authorReviewsLoading: false,
	authorReviewsError: null,
	authorReviewsTotalCount: 0,
	authorReviewsHasNextPage: false,
	// Vote operations
	voteLoading: false,
	voteError: null,
};

const reviewsSlice = createSlice({
	name: "reviews",
	initialState,
	reducers: {
		clearReviewsError(state) { state.error = null; },
		clearCurrentReview(state) { state.current = null; },
		clearUserReviewsError(state) { state.userReviewsError = null; },
		clearAuthorReviewsError(state) { state.authorReviewsError = null; },
		clearVoteError(state) { state.voteError = null; },
		clearUserReviews(state) {
			state.userReviews = [];
			state.userReviewsTotalCount = 0;
			state.userReviewsHasNextPage = false;
		},
		clearAuthorReviews(state) {
			state.authorReviews = [];
			state.authorReviewsTotalCount = 0;
			state.authorReviewsHasNextPage = false;
		},
		clearAllReviews(state) {
			state.items = [];
			state.userReviews = [];
			state.authorReviews = [];
			state.current = null;
			state.totalCount = 0;
			state.userReviewsTotalCount = 0;
			state.authorReviewsTotalCount = 0;
			state.hasNextPage = false;
			state.userReviewsHasNextPage = false;
			state.authorReviewsHasNextPage = false;
		}
	},
	extraReducers: (builder) => {
		builder
			.addCase(createReview.pending, (s) => { s.loading = true; s.error = null; })
			.addCase(createReview.fulfilled, (s, a: PayloadAction<Review>) => { s.loading = false; s.items.unshift(a.payload); s.totalCount = (s.totalCount || 0) + 1; })
			.addCase(createReview.rejected, (s, a: any) => { s.loading = false; s.error = a.payload as string; });

		builder
			.addCase(updateReview.pending, (s) => { s.loading = true; s.error = null; })
			.addCase(updateReview.fulfilled, (s, a: PayloadAction<Review>) => {
				s.loading = false;
				const i = s.items.findIndex(x => x.id === a.payload.id);
				if (i !== -1) s.items[i] = a.payload;
				if (s.current?.id === a.payload.id) s.current = a.payload;
			})
			.addCase(updateReview.rejected, (s, a: any) => { s.loading = false; s.error = a.payload as string; });

		builder
			.addCase(fetchReview.pending, (s) => { s.loading = true; s.error = null; })
			.addCase(fetchReview.fulfilled, (s, a: PayloadAction<Review>) => { s.loading = false; s.current = a.payload; })
			.addCase(fetchReview.rejected, (s, a: any) => { s.loading = false; s.error = a.payload as string; });

		builder
			.addCase(fetchReviews.pending, (s) => { s.loading = true; s.error = null; })
			.addCase(fetchReviews.fulfilled, (s, a: PayloadAction<InfinityPaginationResponse<Review>>) => { 
				s.loading = false; 
				s.items = a.payload.data || []; 
				s.totalCount = a.payload.totalCount || 0; 
				s.hasNextPage = a.payload.hasNextPage || false;
			})
			.addCase(fetchReviews.rejected, (s, a: any) => { s.loading = false; s.error = a.payload as string; });

		builder
			.addCase(deleteReview.pending, (s) => { s.loading = true; s.error = null; })
			.addCase(deleteReview.fulfilled, (s, a: PayloadAction<string>) => { 
				s.loading = false; 
				s.items = s.items.filter(x => x.id !== a.payload); 
				if (s.current?.id === a.payload) s.current = null; 
				s.totalCount = Math.max(0, (s.totalCount || 0) - 1); 
			})
			.addCase(deleteReview.rejected, (s, a: any) => { s.loading = false; s.error = a.payload as string; });

		// User Reviews Extra Reducers
		builder
			.addCase(fetchReviewsByUser.pending, (s) => { s.userReviewsLoading = true; s.userReviewsError = null; })
			.addCase(fetchReviewsByUser.fulfilled, (s, a: PayloadAction<InfinityPaginationResponse<Review>>) => { 
				s.userReviewsLoading = false; 
				s.userReviews = a.payload.data || []; 
				s.userReviewsTotalCount = a.payload.totalCount || 0; 
				s.userReviewsHasNextPage = a.payload.hasNextPage || false;
			})
			.addCase(fetchReviewsByUser.rejected, (s, a: any) => { s.userReviewsLoading = false; s.userReviewsError = a.payload as string; });

		// Author Reviews Extra Reducers
		builder
			.addCase(fetchReviewsByAuthor.pending, (s) => { s.authorReviewsLoading = true; s.authorReviewsError = null; })
			.addCase(fetchReviewsByAuthor.fulfilled, (s, a: PayloadAction<InfinityPaginationResponse<Review>>) => { 
				s.authorReviewsLoading = false; 
				s.authorReviews = a.payload.data || []; 
				s.authorReviewsTotalCount = a.payload.totalCount || 0; 
				s.authorReviewsHasNextPage = a.payload.hasNextPage || false;
			})
			.addCase(fetchReviewsByAuthor.rejected, (s, a: any) => { s.authorReviewsLoading = false; s.authorReviewsError = a.payload as string; });

		// Vote Operations Extra Reducers
		builder
			.addCase(createVote.pending, (s) => { s.voteLoading = true; s.voteError = null; })
			.addCase(createVote.fulfilled, (s, a: PayloadAction<Review>) => {
				s.voteLoading = false;
				// Update the review in all relevant arrays
				const updateReviewInArray = (reviews: Review[]) => {
					const index = reviews.findIndex(r => r.id === a.payload.id);
					if (index !== -1) {
						// Ensure we preserve all properties and update votes array
						reviews[index] = {
							...reviews[index],
							...a.payload,
							votes: a.payload.votes || []
						};
					}
				};
				updateReviewInArray(s.items);
				updateReviewInArray(s.userReviews);
				updateReviewInArray(s.authorReviews);
				if (s.current?.id === a.payload.id) {
					s.current = {
						...s.current,
						...a.payload,
						votes: a.payload.votes || []
					};
				}
			})
			.addCase(createVote.rejected, (s, a: any) => { s.voteLoading = false; s.voteError = a.payload as string; });

		builder
			.addCase(removeVote.pending, (s) => { s.voteLoading = true; s.voteError = null; })
			.addCase(removeVote.fulfilled, (s, a: PayloadAction<Review>) => {
				s.voteLoading = false;
				// Update the review in all relevant arrays
				const updateReviewInArray = (reviews: Review[]) => {
					const index = reviews.findIndex(r => r.id === a.payload.id);
					if (index !== -1) {
						// Ensure we preserve all properties and update votes array
						reviews[index] = {
							...reviews[index],
							...a.payload,
							votes: a.payload.votes || []
						};
					}
				};
				updateReviewInArray(s.items);
				updateReviewInArray(s.userReviews);
				updateReviewInArray(s.authorReviews);
				if (s.current?.id === a.payload.id) {
					s.current = {
						...s.current,
						...a.payload,
						votes: a.payload.votes || []
					};
				}
			})
			.addCase(removeVote.rejected, (s, a: any) => { s.voteLoading = false; s.voteError = a.payload as string; });
	}
});

export const { 
	clearReviewsError, 
	clearCurrentReview, 
	clearUserReviewsError,
	clearAuthorReviewsError,
	clearVoteError,
	clearUserReviews,
	clearAuthorReviews,
	clearAllReviews
} = reviewsSlice.actions;
export default reviewsSlice.reducer;
